// @/components/terms/TermsAcceptanceGate.jsx

/**
 * TermsAcceptanceGate
 *
 * Blocking legal modal mounted at the app shell (gatsby-browser.js
 * wrapPageElement). Fires when the backend `/api/terms-acceptance/check`
 * returns shouldShow=true — i.e., the current user has not yet accepted
 * the active terms version.
 *
 * Non-dismissible by design: no X button, ESC is a no-op, outside-click
 * does not close. The only escapes are Accept (records via API) or logout.
 *
 * Refetches on visibilitychange so a mid-session version bump catches the
 * user on tab focus.
 *
 * Fail-open: any network/error path leaves the user un-gated and the next
 * visibility tick retries. The platform never becomes unreachable due to
 * a backend hiccup.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertBody,
  AlertActions,
} from '@/components/reusable/alert';
import { Button } from '@/components/reusable/button';
import { useLanguage } from '@/components/providers/LanguageContext';
import { authService } from '@/services/auth';

// Hardcoded English fallback. Modal copy now lives on the terms-version
// row (CS authors per-locale via the Terms Version content-type), but
// these defaults kick in when CS hasn't authored a locale yet so a
// fresh release can roll out without all 30 fields filled up front.
const FALLBACK_COPY = {
  title: 'Our Terms have updated',
  body: 'Please review our updated Terms of Use and Privacy Policy and confirm to continue using the platform.',
  termsLinkLabel: 'Terms of Use',
  privacyLinkLabel: 'Privacy Policy',
  acceptButtonLabel: 'Accept',
  // laterButtonLabel intentionally absent — when CS hasn't authored
  // a Later label, gate stays single-Accept (strict mode). Frontend
  // only renders the Later button when CMS provides a non-empty label.
};

const EMPTY_STATE = {
  open: false,
  currentVersion: null,
  termsUrl: null,
  privacyUrl: null,
  title: null,
  body: null,
  termsLinkLabel: null,
  privacyLinkLabel: null,
  acceptButtonLabel: null,
  laterButtonLabel: null,
};

// Session-scoped key used to suppress the gate after a "Later" click.
// Cleared when the tab/session ends — so next login or reopen brings
// the gate back. NOT persisted to localStorage on purpose: that would
// turn Later into a permanent bypass.
const laterFlagKey = (version) => `termsLaterDeferred_${version}`;

const TermsAcceptanceGate = () => {
  const { locale } = useLanguage?.() || { locale: 'en' };
  const [gateState, setGateState] = useState(EMPTY_STATE);
  const [submitting, setSubmitting] = useState(false);

  const handler = typeof window !== 'undefined' ? window.appStore?.termsAcceptanceHandler : null;

  const runCheck = useCallback(async () => {
    if (!handler) return;
    const auth = authService.getAuthState?.();
    if (!auth?.authenticated) return;

    const result = await handler.check(locale);
    if (result?.shouldShow && result.termsUrl && result.privacyUrl) {
      // Session-scoped suppression: if the user clicked Later in this
      // session, skip painting the gate until the next session (tab
      // close + reopen, new login). Server still says shouldShow=true,
      // we just defer rendering.
      if (typeof sessionStorage !== 'undefined') {
        const deferred = sessionStorage.getItem(laterFlagKey(result.currentVersion));
        if (deferred === '1') {
          console.log(`[TermsGate] skip reason=session_deferred version=${result.currentVersion}`);
          setGateState((prev) => (prev.open ? EMPTY_STATE : prev));
          return;
        }
      }

      setGateState({
        open: true,
        currentVersion: result.currentVersion,
        termsUrl: result.termsUrl,
        privacyUrl: result.privacyUrl,
        title: result.title || null,
        body: result.body || null,
        termsLinkLabel: result.termsLinkLabel || null,
        privacyLinkLabel: result.privacyLinkLabel || null,
        acceptButtonLabel: result.acceptButtonLabel || null,
        laterButtonLabel: result.laterButtonLabel || null,
      });
    } else {
      setGateState((prev) => (prev.open ? EMPTY_STATE : prev));
    }
  }, [handler, locale]);

  useEffect(() => {
    runCheck();
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        runCheck();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [runCheck]);

  // Stacking guard: AnnouncementHost reads window.__termsGateOpen to
  // suppress paint while we're showing. Without this, the announcement
  // dialog mounts behind our backdrop and clicks meant for our buttons
  // can hit the wrong layer. We also dispatch a custom event on close
  // so AnnouncementHost can immediately re-run its visibility check
  // rather than waiting for the next visibilitychange tick.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const wasOpen = window.__termsGateOpen === true;
    window.__termsGateOpen = gateState.open;
    // blockingOverlayChanged fires on every flag change (open AND close) —
    // the CookieBanner listens so it can hide while a blocking dialog is
    // swallowing outside clicks. termsGateClosed stays close-only for
    // AnnouncementHost's refresh contract.
    window.dispatchEvent(new CustomEvent('blockingOverlayChanged'));
    if (wasOpen && !gateState.open) {
      window.dispatchEvent(new CustomEvent('termsGateClosed'));
    }
    return () => {
      // On unmount, clear the flag so a stale `true` doesn't trap
      // AnnouncementHost forever.
      if (typeof window !== 'undefined') {
        window.__termsGateOpen = false;
        window.dispatchEvent(new CustomEvent('blockingOverlayChanged'));
      }
    };
  }, [gateState.open]);

  const handleAccept = async () => {
    if (!handler || submitting) return;
    setSubmitting(true);
    try {
      const result = await handler.accept();
      if (result?.recorded || result?.reason === 'already_accepted') {
        setGateState(EMPTY_STATE);
      } else {
        // Server didn't record — keep gate open so user can retry.
        console.warn('[TermsGate] accept did not record, keeping gate open', result);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Later: session-scoped suppression. Deliberately NOT a server-side write
  // (terms-acceptance audit trail stays clean — only Accept clicks create
  // a row). The gate reappears on next session.
  const handleLater = () => {
    if (!gateState.currentVersion) return;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(laterFlagKey(gateState.currentVersion), '1');
      }
    } catch (e) {
      // Storage quota / privacy mode — ignore. In that case the gate
      // simply won't suppress, which is the safer fallback.
    }
    console.log(`[TermsGate] later clicked version=${gateState.currentVersion} (session-scoped)`);
    setGateState(EMPTY_STATE);
  };

  if (!gateState.open) return null;

  // Per-field fallback: each field independently uses CMS-authored value
  // when present, or the hardcoded English default. Means CS can author
  // partial copy (e.g. translate title only, leave labels for later) and
  // the gate still renders cleanly. laterButtonLabel has NO fallback —
  // when CS hasn't authored it, the gate stays strict-mode (single Accept).
  const t = {
    title: gateState.title || FALLBACK_COPY.title,
    body: gateState.body || FALLBACK_COPY.body,
    termsLinkLabel: gateState.termsLinkLabel || FALLBACK_COPY.termsLinkLabel,
    privacyLinkLabel: gateState.privacyLinkLabel || FALLBACK_COPY.privacyLinkLabel,
    acceptButtonLabel: gateState.acceptButtonLabel || FALLBACK_COPY.acceptButtonLabel,
    laterButtonLabel: gateState.laterButtonLabel || null,
  };

  return (
    <Alert
      open={gateState.open}
      onClose={() => {}}
      size="xl"
      dismissible={false}
      zIndex={50}
    >
      {/*
        Title + body support basic HTML (<b>, <em>, <a>) per the
        canonical Strapi content guide. dangerouslySetInnerHTML +
        whitespace-pre-line cooperates: HTML is parsed normally, and
        literal \n characters in CMS strings render as line breaks.
        (Future: re-attempt blocks RTE conversion with proper
        pre-sync data-prep — see api hotfix#77 for context.)
      */}
      <AlertTitle dangerouslySetInnerHTML={{ __html: t.title }} />
      <AlertDescription
        className="whitespace-pre-line"
        dangerouslySetInnerHTML={{ __html: t.body }}
      />
      <AlertBody>
        <ul className="list-disc pl-6 space-y-1 text-base/6 sm:text-sm/6 text-zinc-700">
          <li>
            <a
              href={gateState.termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              {t.termsLinkLabel}
            </a>
          </li>
          <li>
            <a
              href={gateState.privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              {t.privacyLinkLabel}
            </a>
          </li>
        </ul>
      </AlertBody>
      <AlertActions>
        {t.laterButtonLabel && (
          <Button variant="plain" onClick={handleLater} disabled={submitting}>
            {t.laterButtonLabel}
          </Button>
        )}
        <Button color="dark" onClick={handleAccept} disabled={submitting} autoFocus>
          {submitting ? '…' : t.acceptButtonLabel}
        </Button>
      </AlertActions>
    </Alert>
  );
};

export default TermsAcceptanceGate;
